import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Save, Store } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { AutoApplyToggle } from "@/components/AutoApplyToggle";
import { SupplierMargins } from "@/components/SupplierMargins";
import { SuppliersAutoApply } from "@/components/SuppliersAutoApply";
import { api, type MarketplaceAutomation, type MarketplaceAutomationPatch } from "@/lib/api";
import { cn } from "@/lib/utils";

const COUNTRY_ORDER = ["IT", "ES", "FR"];

function countryRank(country: string): number {
  const idx = COUNTRY_ORDER.indexOf(country);
  return idx === -1 ? COUNTRY_ORDER.length : idx;
}

function isValidPercent(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (!/^\d{1,2}([.,]\d{1,2})?$/.test(trimmed)) return false;
  const n = Number(trimmed.replace(",", "."));
  return !Number.isNaN(n) && n >= 0 && n <= 60;
}

function isValidDelta(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (!/^\d{1,2}([.,]\d{1,2})?$/.test(trimmed)) return false;
  const n = Number(trimmed.replace(",", "."));
  return !Number.isNaN(n) && n >= 0 && n <= 50;
}

interface DraftRow {
  min_margin_pct: string;
  target_margin_pct: string;
  buybox_delta: string;
  auto_apply: boolean;
  promo_margin_pct: string;
}

function toDraft(row: MarketplaceAutomation): DraftRow {
  return {
    min_margin_pct: row.min_margin_pct,
    target_margin_pct: row.target_margin_pct,
    buybox_delta: row.buybox_delta,
    auto_apply: row.auto_apply,
    promo_margin_pct: row.promo_margin_pct ?? "",
  };
}

function diffPatch(draft: DraftRow, persisted: MarketplaceAutomation): MarketplaceAutomationPatch {
  const patch: MarketplaceAutomationPatch = {};
  const normMargin = draft.min_margin_pct.trim().replace(",", ".");
  const normTarget = draft.target_margin_pct.trim().replace(",", ".");
  const normDelta = draft.buybox_delta.trim().replace(",", ".");
  if (normMargin && normMargin !== persisted.min_margin_pct) {
    patch.min_margin_pct = normMargin;
  }
  if (normTarget && normTarget !== persisted.target_margin_pct) {
    patch.target_margin_pct = normTarget;
  }
  if (normDelta && normDelta !== persisted.buybox_delta) {
    patch.buybox_delta = normDelta;
  }
  if (draft.auto_apply !== persisted.auto_apply) {
    patch.auto_apply = draft.auto_apply;
  }
  return patch;
}

function describePatch(patch: MarketplaceAutomationPatch, previous: MarketplaceAutomation): string {
  const parts: string[] = [];
  if (patch.min_margin_pct !== undefined) {
    parts.push(`min margine ${previous.min_margin_pct}% → ${patch.min_margin_pct}%`);
  }
  if (patch.target_margin_pct !== undefined) {
    parts.push(`margine obiettivo ${previous.target_margin_pct}% → ${patch.target_margin_pct}%`);
  }
  if (patch.buybox_delta !== undefined) {
    parts.push(`buybox ${previous.buybox_delta}€ → ${patch.buybox_delta}€`);
  }
  if (patch.auto_apply !== undefined) {
    parts.push(
      `auto-apply ${previous.auto_apply ? "ON" : "OFF"} → ${patch.auto_apply ? "ON" : "OFF"}`,
    );
  }
  return parts.join(" · ");
}

function describePromoPatch(value: string, previous: MarketplaceAutomation): string {
  const prev = previous.promo_margin_pct ? `${previous.promo_margin_pct}%` : "nessuno";
  const next = value ? `${value}%` : "nessuno";
  return `margine in promo ${prev} → ${next}`;
}

const NOME_PAESE: Record<string, string> = { IT: "Italia", ES: "Spagna", FR: "Francia" };

export function Marketplaces(): JSX.Element {
  const qc = useQueryClient();
  const automation = useQuery<MarketplaceAutomation[]>({
    queryKey: ["marketplaces-automation"],
    queryFn: api.marketplaceAutomation,
  });
  const [drafts, setDrafts] = useState<Record<string, DraftRow>>({});
  const [saved, setSaved] = useState<{ id: string; summary: string } | null>(null);
  // Il fallimento NON si auto-dismissa e resta ancorato alla riga: e' l'unico
  // segnale che i parametri di repricing NON sono stati scritti. Se sparisse da
  // solo, resterebbe una UI che afferma un valore che il backend non ha.
  const [failed, setFailed] = useState<{ id: string; summary: string; message: string } | null>(
    null,
  );

  useEffect(() => {
    if (!automation.data) return;
    setDrafts((current) => {
      const next = { ...current };
      for (const row of automation.data ?? []) {
        if (next[row.id] === undefined) next[row.id] = toDraft(row);
      }
      return next;
    });
  }, [automation.data]);

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(null), 6000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const grouped = useMemo(() => groupByCountry(automation.data ?? []), [automation.data]);

  const syncMapping = useMutation({
    mutationFn: () => api.syncPoleepoPublications(),
  });

  const save = useMutation({
    mutationFn: ({
      marketplaceId,
      patch,
    }: {
      marketplaceId: string;
      patch: MarketplaceAutomationPatch;
      summary: string;
    }) => api.setMarketplaceAutomation(marketplaceId, patch),
    onSuccess: (result, variables) => {
      qc.setQueryData<MarketplaceAutomation[]>(["marketplaces-automation"], (current) =>
        (current ?? []).map((row) => (row.id === result.id ? result : row)),
      );
      setDrafts((current) => ({
        ...current,
        [result.id]: toDraft(result),
      }));
      setSaved({ id: result.id, summary: variables.summary });
      setFailed((current) => (current?.id === result.id ? null : current));
    },
    onError: (error, variables) => {
      setFailed({
        id: variables.marketplaceId,
        summary: variables.summary,
        message: error instanceof Error ? error.message : "richiesta fallita",
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["marketplaces-automation"] });
    },
  });

  // Endpoint separato da /automation (Task 5): scrive/cancella promo_margin_pct.
  const savePromo = useMutation({
    mutationFn: ({
      marketplaceId,
      value,
    }: {
      marketplaceId: string;
      value: string | null;
      summary: string;
    }) => api.putPromoMargin(marketplaceId, value),
    onSuccess: (result, variables) => {
      qc.setQueryData<MarketplaceAutomation[]>(["marketplaces-automation"], (current) =>
        (current ?? []).map((row) =>
          row.id === variables.marketplaceId
            ? { ...row, promo_margin_pct: result.margin_pct }
            : row,
        ),
      );
      setDrafts((current) => {
        const existing = current[variables.marketplaceId];
        if (!existing) return current;
        return {
          ...current,
          [variables.marketplaceId]: { ...existing, promo_margin_pct: result.margin_pct ?? "" },
        };
      });
      setSaved({ id: variables.marketplaceId, summary: variables.summary });
      setFailed((current) => (current?.id === variables.marketplaceId ? null : current));
    },
    onError: (error, variables) => {
      setFailed({
        id: variables.marketplaceId,
        summary: variables.summary,
        message: error instanceof Error ? error.message : "richiesta fallita",
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["marketplaces-automation"] });
    },
  });

  const isLoading = automation.isLoading;
  const isError = automation.isError;

  return (
    <section className="space-y-6 px-7 pb-8 pt-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl space-y-1">
          <h1 className="page-title">Marketplaces</h1>
          <p className="page-subtitle">
            Gestisci margini, delta buybox e attivazione auto-apply per ogni marketplace collegato.
            {isLoading && " · caricamento…"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="chip whitespace-nowrap">
            <Store className="h-3.5 w-3.5" strokeWidth={1.6} aria-hidden="true" />
            <strong>{automation.data?.filter((row) => row.auto_apply).length ?? 0}</strong> su{" "}
            {automation.data?.length ?? 0} con auto-apply
          </span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => syncMapping.mutate()}
            disabled={syncMapping.isPending}
            title={
              "Scarica da Poleepo l'elenco completo delle pubblicazioni e popola la cache " +
              "SKU -> publication_id (necessaria per push manuale e auto-apply)."
            }
          >
            <RefreshCw
              className={cn("ic", syncMapping.isPending && "animate-spin")}
              strokeWidth={1.6}
              aria-hidden="true"
            />
            {syncMapping.isPending ? "Sync in corso..." : "Sincronizza mapping Poleepo"}
          </button>
        </div>
      </header>

      <SuppliersAutoApply />

      {syncMapping.isSuccess && (
        <div className="rounded-[var(--radius)] border border-info/30 bg-info/5 px-4 py-3 text-md text-info">
          Sync mapping Poleepo avviato in background. Stato visibile nella pagina Storico (filtra
          per <code className="font-mono">publication_sync_*</code>).
        </div>
      )}
      {syncMapping.isError && (
        <div className="rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore avvio sync:{" "}
          {syncMapping.error instanceof Error ? syncMapping.error.message : "richiesta fallita"}
        </div>
      )}

      {isError && (
        <div className="rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Errore caricamento automation:{" "}
          {automation.error instanceof Error ? automation.error.message : "richiesta fallita"}
        </div>
      )}

      <div className="space-y-4">
        {isLoading ? (
          <MarketplacesLoading />
        ) : (
          grouped.map(([country, rows]) => (
            <section key={country} className="card-stack overflow-hidden">
              <div className="flex items-center justify-between border-b border-line bg-paper-2 px-4 py-3">
                <h2 className="flex items-center gap-2 text-md font-semibold text-foreground">
                  <span className="country-flag">
                    <span className="ccode">{country}</span>
                  </span>
                  {NOME_PAESE[country] ?? country}
                </h2>
                <span className="text-label text-ink-3">{rows.length} marketplace</span>
              </div>
              <div className="divide-y divide-line">
                <div className="hidden gap-3 px-4 py-2 text-label font-semibold text-ink-3 md:grid md:grid-cols-[minmax(220px,1fr)_140px_140px_140px_120px_120px]">
                  <span>Marketplace</span>
                  <span>Min margine</span>
                  <span>Marg. obiettivo</span>
                  <span>Buybox −€</span>
                  <span>Auto-apply</span>
                  <span aria-hidden="true" />
                </div>
                {rows.map((marketplace) => {
                  const draft = drafts[marketplace.id] ?? toDraft(marketplace);
                  const validMargin = isValidPercent(draft.min_margin_pct);
                  const validDelta = isValidDelta(draft.buybox_delta);
                  // Margine obiettivo (price-setter): valido e >= margine minimo.
                  const validTarget =
                    !marketplace.is_price_setter ||
                    (isValidPercent(draft.target_margin_pct) &&
                      Number(draft.target_margin_pct.replace(",", ".")) >=
                        Number(draft.min_margin_pct.replace(",", ".")));
                  const patch = diffPatch(draft, marketplace);
                  const dirty = Object.keys(patch).length > 0;
                  // Margine in promo: endpoint dedicato (Task 5), non fa parte del
                  // patch /automation. Vuoto = valido, significa "nessun override".
                  const normPromo = draft.promo_margin_pct.trim().replace(",", ".");
                  const persistedPromo = marketplace.promo_margin_pct ?? "";
                  const promoDirty = marketplace.is_price_setter && normPromo !== persistedPromo;
                  // Come validTarget: sotto il margine MINIMO il numero
                  // digitato non e' quello applicato (clamp_target_to_floor lo
                  // alza in silenzio). Il confronto e' col minimo NEL DRAFT,
                  // non col persistito: lo stesso click scrive entrambi.
                  const validPromo =
                    !normPromo ||
                    (isValidPercent(draft.promo_margin_pct) &&
                      Number(normPromo) >= Number(draft.min_margin_pct.replace(",", ".")));
                  const pending =
                    save.isPending && save.variables?.marketplaceId === marketplace.id;
                  const promoPending =
                    savePromo.isPending && savePromo.variables?.marketplaceId === marketplace.id;
                  const canSave =
                    (dirty || promoDirty) &&
                    validMargin &&
                    validDelta &&
                    validTarget &&
                    validPromo &&
                    !pending &&
                    !promoPending;
                  const marginInputId = `min-margin-${marketplace.id}`;
                  const targetInputId = `target-margin-${marketplace.id}`;
                  const deltaInputId = `buybox-delta-${marketplace.id}`;
                  const promoInputId = `promo-margin-${marketplace.id}`;
                  const problemsId = `problemi-${marketplace.id}`;
                  // Un bordo rosso non dice PERCHE'. Senza questo, il Salva
                  // disabilitato per errore e quello disabilitato perche' non
                  // c'e' nulla da salvare hanno lo stesso identico aspetto.
                  const problems: string[] = [];
                  if (!validMargin) {
                    problems.push("Margine minimo: numero tra 0 e 60.");
                  }
                  if (marketplace.is_price_setter && !validTarget) {
                    problems.push(
                      "Margine obiettivo: numero tra 0 e 60, mai sotto il margine minimo.",
                    );
                  }
                  if (!validDelta) {
                    problems.push("Buybox: numero tra 0 e 50.");
                  }
                  if (marketplace.is_price_setter && !validPromo) {
                    problems.push(
                      "Margine in promo: numero tra 0 e 60, mai sotto il margine minimo, " +
                        "oppure vuoto.",
                    );
                  }
                  const rowFailed = failed?.id === marketplace.id ? failed : null;
                  const toggleDirty = patch.auto_apply !== undefined;
                  // Un solo bottone Salva copre due endpoint (/automation e
                  // /promo-margin): condivisa da bottone principale e Riprova,
                  // cosi' il retry non dimentica meta' del salvataggio fallito.
                  //
                  // SEQUENZIALI, mai in parallelo. Ogni endpoint valida contro
                  // il valore PERSISTITO: lanciate insieme, min=40 e promo=20
                  // passano entrambe (20 >= il vecchio min 12) e lasciano
                  // promo < min, cioe' esattamente cio' che i due 422 esistono
                  // per impedire. In piu' il 200 della promo cancellerebbe il
                  // banner rosso di /automation: fallimento mostrato come
                  // successo. Prima il minimo (e' il pavimento), poi la promo,
                  // che cosi' viene validata contro il minimo appena scritto.
                  const handleSave = async () => {
                    try {
                      if (dirty) {
                        await save.mutateAsync({
                          marketplaceId: marketplace.id,
                          patch,
                          summary: describePatch(patch, marketplace),
                        });
                      }
                      if (promoDirty) {
                        await savePromo.mutateAsync({
                          marketplaceId: marketplace.id,
                          value: normPromo || null,
                          summary: describePromoPatch(normPromo, marketplace),
                        });
                      }
                    } catch {
                      // onError ha gia' ancorato il fallimento alla riga; qui
                      // serve solo fermare la catena senza unhandled rejection.
                    }
                  };

                  return (
                    <div
                      key={marketplace.id}
                      className={cn(
                        "px-4 py-3",
                        // La barra legge il valore PERSISTITO, non il draft: e'
                        // l'unico segnale di cosa sta davvero facendo il sistema.
                        marketplace.auto_apply && "row-auto-on",
                      )}
                    >
                      <div className="grid gap-3 md:grid-cols-[minmax(220px,1fr)_140px_140px_140px_120px_120px]">
                        <div className="min-w-0">
                          <label
                            htmlFor={marginInputId}
                            className="block truncate text-md font-semibold text-foreground"
                          >
                            {marketplace.label}
                          </label>
                          <div className="mt-0.5 font-code text-micro text-ink-3">
                            {marketplace.id}
                          </div>
                        </div>

                        <div
                          className={cn(
                            "control-shell h-10 px-3",
                            patch.min_margin_pct && "border-key-press bg-key/15",
                            !validMargin && "border-destructive/50 bg-destructive/5",
                          )}
                        >
                          <span className="mr-2 shrink-0 text-micro font-semibold text-ink-3 md:hidden">
                            Min marg.
                          </span>
                          <input
                            id={marginInputId}
                            aria-label={`${marketplace.label} margine minimo`}
                            aria-invalid={!validMargin}
                            aria-describedby={problems.length ? problemsId : undefined}
                            value={draft.min_margin_pct}
                            onChange={(event) =>
                              setDrafts((current) => ({
                                ...current,
                                [marketplace.id]: {
                                  ...(current[marketplace.id] ?? toDraft(marketplace)),
                                  min_margin_pct: event.target.value,
                                },
                              }))
                            }
                            inputMode="decimal"
                            className="w-full bg-transparent font-mono text-sm tabular-nums outline-none"
                          />
                          <span className="font-mono text-xs text-muted-foreground">%</span>
                        </div>

                        {marketplace.is_price_setter ? (
                          <div
                            className={cn(
                              "control-shell h-10 px-3",
                              patch.target_margin_pct && "border-key-press bg-key/15",
                              !validTarget && "border-destructive/50 bg-destructive/5",
                            )}
                            title="Il margine a cui AM fissa il prezzo. Deve essere >= del margine minimo."
                          >
                            <span className="mr-2 shrink-0 text-micro font-semibold text-ink-3 md:hidden">
                              Obiettivo
                            </span>
                            <input
                              id={targetInputId}
                              aria-label={`${marketplace.label} margine obiettivo`}
                              aria-invalid={!validTarget}
                              aria-describedby={problems.length ? problemsId : undefined}
                              value={draft.target_margin_pct}
                              onChange={(event) =>
                                setDrafts((current) => ({
                                  ...current,
                                  [marketplace.id]: {
                                    ...(current[marketplace.id] ?? toDraft(marketplace)),
                                    target_margin_pct: event.target.value,
                                  },
                                }))
                              }
                              inputMode="decimal"
                              className="w-full bg-transparent font-mono text-sm tabular-nums outline-none"
                            />
                            <span className="font-mono text-xs text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <div className="flex h-10 items-center px-3 font-mono text-xs text-muted-foreground">
                            —
                          </div>
                        )}

                        <div
                          className={cn(
                            "control-shell h-10 px-3",
                            patch.buybox_delta && "border-key-press bg-key/15",
                            !validDelta && "border-destructive/50 bg-destructive/5",
                          )}
                        >
                          <span className="mr-2 shrink-0 text-micro font-semibold text-ink-3 md:hidden">
                            Buybox
                          </span>
                          <input
                            id={deltaInputId}
                            aria-label={`${marketplace.label} buybox delta`}
                            aria-invalid={!validDelta}
                            aria-describedby={problems.length ? problemsId : undefined}
                            value={draft.buybox_delta}
                            onChange={(event) =>
                              setDrafts((current) => ({
                                ...current,
                                [marketplace.id]: {
                                  ...(current[marketplace.id] ?? toDraft(marketplace)),
                                  buybox_delta: event.target.value,
                                },
                              }))
                            }
                            inputMode="decimal"
                            className="w-full bg-transparent font-mono text-sm tabular-nums outline-none"
                          />
                          <span className="font-mono text-xs text-muted-foreground">€</span>
                        </div>

                        <div className="flex items-center gap-3">
                          <AutoApplyToggle
                            value={draft.auto_apply}
                            marketplaceLabel={marketplace.label}
                            kind={marketplace.kind}
                            minMarginPct={marketplace.min_margin_pct}
                            ariaLabel={`Auto-apply ${marketplace.label}`}
                            onChange={(next) =>
                              setDrafts((current) => ({
                                ...current,
                                [marketplace.id]: {
                                  ...(current[marketplace.id] ?? toDraft(marketplace)),
                                  auto_apply: next,
                                },
                              }))
                            }
                          />
                          <span className="text-xs font-semibold text-ink-3">
                            {draft.auto_apply ? "ON" : "OFF"}
                            {toggleDirty && (
                              <span className="block normal-case tracking-normal text-ink-2">
                                non salvato
                              </span>
                            )}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={!canSave}
                            onClick={() => void handleSave()}
                            className="btn btn-primary justify-center"
                          >
                            <Save className="ic" strokeWidth={1.6} aria-hidden="true" />
                            {pending || promoPending ? "Salvataggio…" : "Salva"}
                            <span className="sr-only"> {marketplace.label}</span>
                          </button>
                        </div>
                      </div>
                      {problems.length > 0 && (
                        <p id={problemsId} className="mt-2 font-mono text-label text-destructive">
                          {problems.join(" ")}
                        </p>
                      )}
                      {saved?.id === marketplace.id && (
                        <p
                          className="text-accent-ink mt-2 font-mono text-label"
                          aria-live="polite"
                        >
                          Salvato · {saved.summary}
                        </p>
                      )}
                      {rowFailed && (
                        <div
                          role="alert"
                          aria-live="assertive"
                          className="banner danger mt-2 flex flex-wrap items-center gap-x-3 gap-y-1"
                        >
                          <span>
                            <strong>Non salvato.</strong> {rowFailed.summary} — {rowFailed.message}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => void handleSave()}
                            disabled={!canSave}
                          >
                            Riprova
                          </button>
                        </div>
                      )}
                      {marketplace.is_price_setter && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
                          <label
                            htmlFor={promoInputId}
                            className="text-label font-semibold text-ink-3"
                          >
                            Margine in promo
                          </label>
                          <div
                            className={cn(
                              "control-shell h-9 w-28 px-3",
                              promoDirty && "border-key-press bg-key/15",
                              !validPromo && "border-destructive/50 bg-destructive/5",
                            )}
                          >
                            <input
                              id={promoInputId}
                              aria-label={`${marketplace.label} margine in promo`}
                              aria-invalid={!validPromo}
                              aria-describedby={problems.length ? problemsId : undefined}
                              placeholder={marketplace.target_margin_pct}
                              value={draft.promo_margin_pct}
                              onChange={(event) =>
                                setDrafts((current) => ({
                                  ...current,
                                  [marketplace.id]: {
                                    ...(current[marketplace.id] ?? toDraft(marketplace)),
                                    promo_margin_pct: event.target.value,
                                  },
                                }))
                              }
                              inputMode="decimal"
                              className="w-full bg-transparent font-mono text-sm tabular-nums outline-none"
                            />
                            <span className="font-mono text-xs text-muted-foreground">%</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            si applica quando il fornitore sconta il costo
                          </span>
                        </div>
                      )}
                      {marketplace.is_price_setter && (
                        <SupplierMargins
                          marketplaceId={marketplace.id}
                          marketplaceLabel={marketplace.label}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </section>
  );
}

function groupByCountry(rows: MarketplaceAutomation[]): Array<[string, MarketplaceAutomation[]]> {
  const grouped = new Map<string, MarketplaceAutomation[]>();
  for (const row of rows) {
    const list = grouped.get(row.country) ?? [];
    list.push(row);
    grouped.set(row.country, list);
  }
  return Array.from(grouped.entries())
    .sort(([a], [b]) => countryRank(a) - countryRank(b) || a.localeCompare(b))
    .map(([country, list]) => [
      country,
      list.slice().sort((a, b) => a.label.localeCompare(b.label)),
    ]);
}

function MarketplacesLoading(): JSX.Element {
  return (
    <div className="card-stack overflow-hidden">
      <div className="h-11 border-b border-line bg-paper-2 px-4 py-3">
        <div className="bg-paper-3 h-4 w-10 animate-pulse rounded" />
      </div>
      <div className="divide-y divide-line">
        {Array.from({ length: 4 }, (_, index) => (
          <div
            key={index}
            className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(220px,1fr)_140px_140px_140px_120px_120px]"
          >
            <div className="space-y-2">
              <div className="h-5 w-48 animate-pulse rounded bg-paper-2" />
              <div className="h-3 w-36 animate-pulse rounded bg-paper-2" />
            </div>
            <div className="h-10 animate-pulse rounded bg-paper-2" />
            <div className="h-10 animate-pulse rounded bg-paper-2" />
            <div className="h-10 animate-pulse rounded bg-paper-2" />
            <div className="h-10 animate-pulse rounded bg-paper-2" />
            <div className="h-10 animate-pulse rounded bg-paper-2" />
          </div>
        ))}
      </div>
    </div>
  );
}
